/**
 * Processes (Документооборот / EDO) — список заявок, согласований и задач.
 *
 * Live wiring (2026-06-25): the list and drawer read the real `/edo/*` API directly — no demo
 * fallback; an empty or unreachable response shows the empty state:
 *  • «Мои заявки» → GET /edo/requests, «Ждут моего решения» → GET /edo/tasks,
 *    участие в любой роли → GET /edo/participating (сборка и дедуп — useProcessList)
 *  • drawer opens the live request card (GET /edo/requests/:id)
 *
 * Макет «Процессы — фильтр» (16.10.2026) задаёт состав и навигацию, оформление — портала:
 *  • шапка: «Новый процесс» (отдельный маршрут /processes/new) и для HR — «Дашборд подбора»
 *    (/processes/recruitment); в ряду фильтров действий больше нет;
 *  • вкладки по роли со своими счётчиками, по умолчанию «Все»; смена вкладки сбрасывает статус;
 *  • поиск + тип + чипы статуса комбинируются; счётчик чипа считается по тому же набору, что
 *    видит список (вкладка → поиск/тип), так что число на чипе = числу строк под ним;
 *  • узкое окно (< 820): сегмент «Ждут меня · Мои · Все», тип — в нижней панели «Фильтры».
 *
 * Строки, чипы и список — в `src/features/edo/` (processVM + components/): карточка «Все процессы»
 * на Home рисует те же компоненты, только без вкладок и поиска.
 */
import { useMemo, useState } from 'react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { AnimatePresence, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../src/i18n'
import { useResponsive } from '../../../src/lib/responsive'
import { palette } from '../../../src/theme/palette'
import { tone } from '../../../src/theme/tones'
import { PROCESS_TYPES, typeOf } from '../../../src/features/edo/registry'
import { Button, Card, Chip, Icon, IconButton, Link, OptionSelect, QueryState, SearchField, Seg } from '../../../src/ui'
import { pressable } from '../../../src/ui/primitives'
import { Page } from '../../../src/ui/Screen'
import { matchesQuery, statusCounts, type ProcVM } from '../../../src/features/edo/processVM'
import { useProcessList } from '../../../src/features/edo/useProcessList'
import { STATUS_CHIPS, StatusChips } from '../../../src/features/edo/components/StatusChips'
import { ProcessDrawer, useOpenProcess } from '../../../src/features/edo/components/ProcessDrawer'
import { inFilter, ProcessListWithFilters, type Filter } from '../../../src/features/edo/components/ProcessListWithFilters'
import { useMyEdoRoles } from '../../../src/features/edo/useEdo'
import { useMe } from '../../../src/features/users/useUsers'
import { hasAnyRole } from '../../../src/auth/roles'

type Tab = 'waiting' | 'mine' | 'signing' | 'all'
// «Все» — первой (владелец, 29.10.2026); по умолчанию экран и так открывается на ней.
const TABS: Tab[] = ['all', 'waiting', 'mine', 'signing']
const TAB_LABEL: Record<Tab, string> = { waiting: 'proc.tab.waiting', mine: 'proc.tab.mine', signing: 'proc.tab.signing', all: 'doc.tabAll' }
/** Подписи сегмента на телефоне; «Согласую / подписываю» там нет — его строки видны во «Все». */
const TAB_SHORT: Record<Tab, string> = { waiting: 'proc.tabShort.waiting', mine: 'proc.tabShort.mine', signing: 'proc.tab.signing', all: 'doc.tabAll' }
const isTab = (v: unknown): v is Tab => TABS.includes(v as Tab)
/** Сколько строк без архива (отменённых) — так считают вкладки, «Все» и «Показано … из …». */
const live = (vms: ProcVM[]) => vms.filter((p) => inFilter(p, 'all')).length

export default function ProcessesScreen() {
  const { l, lp, t } = useApp()
  const router = useRouter()
  const { width } = useResponsive()
  const narrow = width < 820
  // ?tab=mine — после отправки формы «Нового процесса» открываемся на «Моих заявках».
  const params = useLocalSearchParams<{ tab?: string }>()
  // Дашборд подбора (ФТ-13): бэк пускает admin и ЭДО-роль hr (роль из должности/оверрайда,
  // не realm-роль Keycloak) — тот же признак берём из /edo/my-roles.
  const { data: me } = useMe()
  const { data: edoRoles } = useMyEdoRoles()
  const isHr = hasAnyRole(me, ['admin']) || !!edoRoles?.roles.includes('hr')

  const [tab, setTab] = useState<Tab>(() => (isTab(params.tab) ? params.tab : 'all'))
  const [status, setStatus] = useState<Filter>('all')
  const [type, setType] = useState('all')
  const [query, setQuery] = useState('')
  const [sheet, setSheet] = useState(false)
  const [sel, setSel] = useState<ProcVM | null>(null)
  const openProcess = useOpenProcess(setSel)

  // Live data only — no demo fallback. Участие нужно всегда: счётчики вкладок видны сразу.
  const { reqVMs, taskVMs, signingVMs, allVMs, queries } = useProcessList({ participating: true })
  const loading = queries.requests.isLoading || queries.tasks.isLoading || queries.participating.isLoading

  const view: Tab = narrow && tab === 'signing' ? 'all' : tab
  const sources: Record<Tab, ProcVM[]> = { waiting: taskVMs, mine: reqVMs, signing: signingVMs, all: allVMs }
  const source = sources[view]
  // Вкладка → тип → поиск; статус — последним (его чипы считаются по `base`).
  const base = useMemo(
    () => source.filter((p) => (type === 'all' || p.type === type) && matchesQuery(p, query, t)),
    [source, type, query, t],
  )
  const counts = useMemo(() => statusCounts(base), [base])
  const shown = status === 'all' ? live(base) : counts[status] ?? 0
  // Архив — своя «папка»: «Показано … из …» в нём считается по отменённым вкладки.
  const total = status === 'cancelled' ? source.length - live(source) : live(source)

  // «Тип»: виды, которые реально встречаются в моих процессах, — в порядке реестра, неизвестные в конце.
  const typeOptions = useMemo(() => {
    const present = new Set(allVMs.map((p) => p.type))
    const known = PROCESS_TYPES.map((pt) => pt.id).filter((id) => present.has(id))
    const ids = [...known, ...[...present].filter((id) => !known.includes(id))]
    return [{ value: 'all', label: l('proc.allTypes') }, ...ids.map((id) => ({ value: id, label: t(typeOf(id).name) }))]
  }, [allVMs, l, t])

  const pickTab = (k: Tab) => {
    setTab(k)
    setStatus('all')
  }
  const reset = () => {
    setStatus('all')
    setType('all')
    setQuery('')
  }
  const statusLabel = l(STATUS_CHIPS.find((c) => c.key === status)?.labelKey ?? '')
  const tags = [
    status !== 'all' && { key: 'status', label: `${l('proc.status')}: ${statusLabel}`, clear: () => setStatus('all') },
    type !== 'all' && { key: 'type', label: `${l('proc.type')}: ${t(typeOf(type).name)}`, clear: () => setType('all') },
    query.trim() && { key: 'q', label: `«${query.trim()}»`, clear: () => setQuery('') },
  ].filter((x): x is { key: string; label: string; clear: () => void } => !!x)
  // Бейдж кнопки «Фильтры» на телефоне: статус и тип (поиск и так виден в поле).
  const filterCount = (status !== 'all' ? 1 : 0) + (type !== 'all' ? 1 : 0)

  const openNew = () => router.push('/processes/new')
  const openDashboard = () => router.push('/processes/recruitment')
  const gold = tone('gold')

  const action = narrow ? (
    <XStack gap={8}>
      {isHr && (
        <IconButton width={44} height={44} borderWidth={1} borderColor="$borderColor" aria-label={l('proc.dashboard')} onPress={openDashboard}>
          <Icon name="users" size={18} color="$color" />
        </IconButton>
      )}
      <IconButton
        width={44}
        height={44}
        backgroundColor="$primary"
        hoverStyle={{ backgroundColor: '$primaryActive' }}
        aria-label={l('doc.new')}
        onPress={openNew}
      >
        <Icon name="plus" size={20} color="$onPrimary" />
      </IconButton>
    </XStack>
  ) : (
    <XStack gap={8} flexWrap="wrap">
      {isHr && (
        <Button icon={<Icon name="users" size={15} color="$color" />} onPress={openDashboard}>
          {l('proc.dashboard')}
        </Button>
      )}
      <Button variant="primary" icon={<Icon name="plus" size={15} color="$onPrimary" />} onPress={openNew}>
        {l('doc.new')}
      </Button>
    </XStack>
  )

  const empty = tags.length ? (
    <Card padding={40} alignItems="center" gap={10}>
      <Text fontSize={15} fontWeight="700" color="$color">
        {l('proc.notFound')}
      </Text>
      <Text fontSize={13} color="$muted" textAlign="center" maxWidth={480}>
        {l('proc.notFoundHint').replace('{tab}', l(TAB_LABEL[view]))}
      </Text>
      <Button marginTop={6} height={narrow ? 44 : 36} onPress={reset}>
        {l('proc.resetFilters')}
      </Button>
    </Card>
  ) : undefined

  return (
    <>
      {/* телефон: без подзаголовка — иначе кнопки уезжают под него на свою строку */}
      <Page title={l('proc.title')} subtitle={narrow ? undefined : l('proc.subtitle')} action={action}>
        {taskVMs.length > 0 && view !== 'waiting' ? (
          <Card padding={14} flexDirection="row" alignItems="center" gap={12} flexWrap="wrap" backgroundColor={gold.soft} borderColor={gold.solid}>
            <Stack width={36} height={36} flexShrink={0} borderRadius={999} backgroundColor="$background" alignItems="center" justifyContent="center">
              <Icon name="clock" size={18} color={gold.text} />
            </Stack>
            <YStack flexGrow={1} flexShrink={1} flexBasis={200} gap={2}>
              <Text fontSize={14} fontWeight="700" color="$color">
                {lp('proc.banner', taskVMs.length)}
              </Text>
              <Text fontSize={12} color="$muted">
                {l('proc.bannerHint')}
              </Text>
            </YStack>
            <Button variant="primary" height={narrow ? 44 : 36} onPress={() => pickTab('waiting')}>
              {l('proc.show')}
            </Button>
          </Card>
        ) : null}

        <XStack
          role="tablist"
          aria-label={l('proc.tabs')}
          alignSelf={narrow ? 'stretch' : 'flex-start'}
          gap={4}
          backgroundColor="$bgLayout"
          padding={4}
          borderRadius={7}
          flexWrap={narrow ? 'nowrap' : 'wrap'}
        >
          {(narrow ? TABS.filter((k) => k !== 'signing') : TABS).map((k) => {
            const n = live(sources[k])
            // Телефон: в сегменте место только под срочный счётчик «Ждут меня».
            const badge = loading || (narrow && (k !== 'waiting' || !n)) ? undefined : n
            return (
              <Seg
                key={k}
                active={view === k}
                label={l(narrow ? TAB_SHORT[k] : TAB_LABEL[k])}
                badge={badge}
                badgeTone={k === 'waiting' && n > 0 ? 'danger' : view === k ? 'primary' : 'muted'}
                onPress={() => pickTab(k)}
                {...(narrow ? { flex: 1, minHeight: 44, justifyContent: 'center', paddingHorizontal: 8 } : null)}
              />
            )
          })}
        </XStack>

        {narrow ? (
          <XStack gap={8}>
            <SearchField value={query} onChange={setQuery} placeholder={l('top.searchShort')} label={l('top.searchShort')} height={44} />
            <IconButton
              width={44}
              height={44}
              borderWidth={1}
              borderColor={filterCount ? '$primary' : '$borderColor'}
              backgroundColor={filterCount ? '$primaryBg' : '$background'}
              aria-label={filterCount ? l('proc.filtersN').replace('{n}', String(filterCount)) : l('proc.filters')}
              onPress={() => setSheet(true)}
            >
              <Icon name="sliders-horizontal" size={18} color={filterCount ? '$primary' : '$muted'} />
              {filterCount ? (
                <Stack position="absolute" top={-6} right={-6} minWidth={18} height={18} paddingHorizontal={5} borderRadius={999} backgroundColor="$primary" alignItems="center" justifyContent="center">
                  <Text fontSize={11} fontWeight="700" color="$onPrimary">
                    {filterCount}
                  </Text>
                </Stack>
              ) : null}
            </IconButton>
          </XStack>
        ) : (
          <XStack gap={12} flexWrap="wrap" alignItems="center" zIndex={10}>
            <XStack flex={1} minWidth={260}>
              <SearchField value={query} onChange={setQuery} placeholder={l('proc.searchPh')} label={l('top.searchShort')} />
            </XStack>
            <OptionSelect list options={typeOptions} value={[type]} onChange={(v) => setType(v[0] ?? 'all')} />
          </XStack>
        )}

        <StatusChips counts={counts} total={live(base)} value={status} onChange={setStatus} loading={loading} />

        {loading ? null : (
          <XStack alignItems="center" columnGap={8} rowGap={6} flexWrap="wrap" marginTop={-6}>
            <Text fontSize={13} color="$muted">
              {shown === total
                ? l('proc.shownAll').replace('{n}', String(shown))
                : l('proc.shown').replace('{n}', String(shown)).replace('{total}', String(total))}
            </Text>
            {narrow
              ? null
              : tags.map((tg) => (
                  <XStack
                    key={tg.key}
                    {...pressable}
                    onPress={tg.clear}
                    aria-label={`${l('proc.removeFilter')}: ${tg.label}`}
                    cursor="pointer"
                    alignItems="center"
                    gap={6}
                    minHeight={28}
                    paddingHorizontal={10}
                    borderRadius={999}
                    backgroundColor="$primaryBg"
                    hoverStyle={{ opacity: 0.85 }}
                  >
                    <Text fontSize={12} fontWeight="600" color="$primary">
                      {tg.label}
                    </Text>
                    <Icon name="x" size={12} color="$primary" />
                  </XStack>
                ))}
            {tags.length ? (
              <Link fontSize={13} paddingVertical={narrow ? 12 : 0} onPress={reset}>
                {l(narrow ? 'proc.reset' : 'proc.resetAll')}
              </Link>
            ) : null}
          </XStack>
        )}

        {/* три источника — секция гаснет, если упал любой (какой именно, тот и отдаём в q) */}
        <QueryState q={[queries.requests, queries.tasks, queries.participating].find((q) => q.isError) ?? queries.requests}>
          <ProcessListWithFilters source={base} onOpen={openProcess} cascadeKey={view} filter={status} empty={empty} />
        </QueryState>
      </Page>

      {/* Телефон: тип процесса — в нижней панели; список за ней обновляется сразу */}
      <AnimatePresence>
        {narrow && sheet ? (
          <Stack key="filters" position="absolute" top={0} left={0} right={0} bottom={0} zIndex={100}>
            <Stack
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              backgroundColor={palette.scrimSoft}
              onPress={() => setSheet(false)}
              animation="quicker"
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
              opacity={1}
            />
            <YStack
              role="dialog"
              aria-modal
              aria-label={l('proc.filters')}
              position="absolute"
              left={0}
              right={0}
              bottom={0}
              gap={14}
              padding={16}
              paddingTop={10}
              paddingBottom={24}
              backgroundColor="$background"
              borderTopLeftRadius={14}
              borderTopRightRadius={14}
              shadowColor={palette.shadow}
              shadowOpacity={0.18}
              shadowRadius={24}
              shadowOffset={{ width: 0, height: -8 }}
              animation="quicker"
              enterStyle={{ y: 400 }}
              exitStyle={{ y: 400 }}
              y={0}
            >
              <Stack alignSelf="center" width={40} height={4} borderRadius={999} backgroundColor="$borderColor" />
              <XStack alignItems="center" justifyContent="space-between">
                <Text fontSize={17} fontWeight="700" color="$color">
                  {l('proc.filters')}
                </Text>
                <Link fontSize={14} paddingVertical={12} onPress={reset}>
                  {l('proc.reset')}
                </Link>
              </XStack>
              <YStack gap={8}>
                <Text fontSize={13} color="$muted">
                  {l('proc.processType')}
                </Text>
                <XStack flexWrap="wrap" gap={8}>
                  {typeOptions.map((o) => (
                    <Chip key={o.value} on={type === o.value} dim={type !== o.value} aria-pressed={type === o.value} minHeight={44} alignItems="center" onPress={() => setType(o.value)}>
                      {o.label}
                    </Chip>
                  ))}
                </XStack>
              </YStack>
              <Button variant="primary" height={48} onPress={() => setSheet(false)}>
                {lp('proc.showN', shown)}
              </Button>
            </YStack>
          </Stack>
        ) : null}
      </AnimatePresence>

      <ProcessDrawer p={sel} onClose={() => setSel(null)} />
    </>
  )
}
